import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface AttendanceRecord {
  id?: number;
  staff_id: number;
  staff_name?: string;
  work_date: string;
  clock_in: string | null;
  clock_out: string | null;
  break_start: string | null;
  break_end: string | null;
  notes: string;
}

interface AttendanceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: AttendanceRecord | null;
  staffList: Array<{ id: number; name: string }>;
  onSave: (data: AttendanceRecord) => Promise<void>;
  mode: 'edit' | 'create';
}

const AttendanceEditModal: React.FC<AttendanceEditModalProps> = ({
  isOpen,
  onClose,
  record,
  staffList,
  onSave,
  mode
}) => {
  const [formData, setFormData] = useState<AttendanceRecord>({
    staff_id: 0,
    work_date: '',
    clock_in: null,
    clock_out: null,
    break_start: null,
    break_end: null,
    notes: ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (record) {
      setFormData({
        ...record,
        clock_in: record.clock_in ? record.clock_in.slice(0, 16) : null,
        clock_out: record.clock_out ? record.clock_out.slice(0, 16) : null,
        break_start: record.break_start ? record.break_start.slice(0, 16) : null,
        break_end: record.break_end ? record.break_end.slice(0, 16) : null,
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        staff_id: staffList[0]?.id || 0,
        work_date: today,
        clock_in: null,
        clock_out: null,
        break_start: null,
        break_end: null,
        notes: ''
      });
    }
  }, [record, staffList]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.staff_id || !formData.work_date || !formData.clock_in) {
      alert('スタッフ、日付、出勤時刻は必須です');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('保存エラー:', error);
      alert('保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">
            {mode === 'edit' ? '勤怠記録を編集' : '勤怠記録を追加'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                スタッフ<span className="text-red-500">*</span>
              </label>
              <select
                value={formData.staff_id}
                onChange={(e) => setFormData({ ...formData, staff_id: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {staffList.map(staff => (
                  <option key={staff.id} value={staff.id}>{staff.name}</option>
                ))}
              </select>
            </div>
          )}

          {mode === 'edit' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">スタッフ</label>
              <div className="px-3 py-2 bg-gray-100 rounded-md">{formData.staff_name || 'N/A'}</div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              日付<span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.work_date}
              onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={mode === 'edit'}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                出勤時刻<span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.clock_in || ''}
                onChange={(e) => setFormData({ ...formData, clock_in: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">退勤時刻</label>
              <input
                type="datetime-local"
                value={formData.clock_out || ''}
                onChange={(e) => setFormData({ ...formData, clock_out: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">休憩開始</label>
              <input
                type="datetime-local"
                value={formData.break_start || ''}
                onChange={(e) => setFormData({ ...formData, break_start: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">休憩終了</label>
              <input
                type="datetime-local"
                value={formData.break_end || ''}
                onChange={(e) => setFormData({ ...formData, break_end: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">メモ・修正理由</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="例：打刻忘れのため手動で入力"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              disabled={loading}
            >
              {loading ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendanceEditModal;
